import { useState, useRef, useEffect } from "react";
import {
  Box,
  Stack,
  Button,
  Text,
  TextInput,
  Inline,
  SelectLegacy,
} from "@compono/ui";
import { PaperplaneSolidIcon, UserOutlineIcon } from "@compono/ui-icons";

// Message type definition
export interface Message {
  id: string;
  text: string;
  sender: "Human" | "AI";
  timestamp: Date;
}

export interface ChatAction {
  /**
   * Text shown on the action button
   */
  label: string;
  /**
   * Message text to send when clicked. If omitted, the label will be used.
   */
  message?: string;
}

interface ChatComponentProps {
  initialMessages?: Message[];
  /**
   * onSendMessage should return the assistant response text (or nothing).
   * If it returns a Promise, a temporary "Thinking..." message will be shown
   * and replaced with the resolved text.
   */
  onSendMessage?: (
    message: string,
    model?: string,
    messageHistory?: Message[],
  ) => Promise<string | void> | string | void;
  /**
   * Optional predefined action buttons.
   */
  actions: ChatAction[];
  /**
   * Whether to send message history with each request
   */
  sendMessageHistory?: boolean;
}
const formatTime = (date: Date) => {
  return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
};

export const ChatComponent: React.FC<ChatComponentProps> = ({
  initialMessages = [],
  onSendMessage,
  actions,
  sendMessageHistory = false,
}) => {
  const [messages, setMessages] = useState<Message[]>(initialMessages);
  const MODEL_OPTIONS = [
    {
      label: "Nova pro",
      value: "amazon.nova-pro-v1:0",
      selected: true,
    },
    {
      label: "Claude Sonnet 4",
      value: "au.anthropic.claude-sonnet-4-6",
    },
    {
      label: "Nova Premier",
      value: "us.amazon.nova-premier-v1:0",
    },

    // Added GPT 120 (placeholder value namespace assumed)
    {
      label: "GPT 120",
      value: "openai.gpt-oss-120b-1:0",
    },
    // Added Llama model (choose a representative value string)
    {
      label: "Llama",
      value: "meta.llama3-1-70b-instruct-v1:0",
    },
  ];
  const [model, setModel] = useState<string>(MODEL_OPTIONS[0].value);
  const [newMessage, setNewMessage] = useState("");
  const [messageHistory, setMessageHistory] = useState<string[]>([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  // Container ref used to control scroll position
  const messagesContainerRef = useRef<HTMLDivElement>(null);

  // Auto scroll so the last message aligns to the top of the viewport (not the bottom of the list)
  useEffect(() => {
    if (!messages.length) return;
    const container = messagesContainerRef.current;
    if (!container) return;
    const messageNodes = container.querySelectorAll('[data-message="true"]');
    const last = messageNodes[messageNodes.length - 1] as
      | HTMLElement
      | undefined;
    if (last) {
      // Scroll so the last message sits at the top of the scroll container
      container.scrollTo({ top: last.offsetTop, behavior: "smooth" });
    }
  }, [messages]);

  const handleSendMessage = async (overrideText?: string) => {
    const outgoing = (overrideText ?? newMessage).trim();
    if (outgoing === "") return;

    const userMessage: Message = {
      id: `msg_${Date.now()}`,
      text: outgoing,
      sender: "Human",
      timestamp: new Date(),
    };

    // Optimistically append user message
    setMessages((prev) => [...prev, userMessage]);

    // Add the message to history and reset the history index
    setMessageHistory((prev) => [outgoing, ...prev]);
    setHistoryIndex(-1);

    setNewMessage("");

    if (onSendMessage) {
      try {
        const pendingId = `pending_${Date.now()}`;
        // Insert a temporary assistant placeholder
        const thinkingMessage: Message = {
          id: pendingId,
          text: "Thinking...",
          sender: "AI",
          timestamp: new Date(),
        };

        // Store temporary thinking message only in UI, not in message history
        setMessages((prev) => [...prev, thinkingMessage]);

        // Send message history (without the thinking message, system messages, and initial messages) if enabled, otherwise just the current message
        // Only include messages that were added during this session (excluding initialMessages)
        // Filter out system messages from history
        const filteredMessages = messages.filter(
          (msg) => !msg.id.startsWith("system_"),
        );
        const messageHistoryToSend =
          filteredMessages.length > initialMessages.length
            ? [...filteredMessages.slice(initialMessages.length), userMessage] // Skip initial messages and include the user message we just added
            : [userMessage]; // If no new messages yet, just send the current user message
        const result = sendMessageHistory
          ? await onSendMessage(outgoing, model, messageHistoryToSend)
          : await onSendMessage(outgoing, model);

        const assistantText =
          (typeof result === "string" && result.length > 0
            ? result
            : "(No response received)") || "(No response received)";

        // Create a new AI message with the response
        const aiMessage: Message = {
          id: `ai_${Date.now()}`,
          text: assistantText,
          sender: "AI",
          timestamp: new Date(),
        };

        // Replace temporary thinking message with the actual AI response message
        setMessages((prev) =>
          prev.map((m) => (m.id === pendingId ? aiMessage : m)),
        );
      } catch (err: unknown) {
        const errorText =
          err instanceof Error ? err.message : "An unexpected error occurred";
        setMessages((prev) => [
          ...prev,
          {
            id: `err_${Date.now()}`,
            text: `Error: ${errorText}`,
            sender: "AI",
            timestamp: new Date(),
          },
        ]);
      }
    }
  };

  return (
    <Box
      sx={{
        height: "800px",
        border: "1px solid",
        borderColor: "brandDark.20",
        borderRadius: "8px",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Model selector */}
      <Box sx={{ px: 3, pt: 3 }}>
        <Inline gap="3" alignY="center">
          <Text size="xs" sx={{ fontWeight: 600, color: "brandDark.60" }}>
            Model
          </Text>
          <Box sx={{ width: 260 }}>
            <SelectLegacy
              options={MODEL_OPTIONS.map((o) => ({
                ...o,
                selected: o.value === model,
              }))}
              onChange={(opt) => {
                if (!Array.isArray(opt)) {
                  const newModel = opt.value as string;

                  // Add system message about model change
                  if (model !== newModel) {
                    // Find selected model details
                    const modelOption = MODEL_OPTIONS.find(
                      (o) => o.value === newModel,
                    );

                    // Create system message for model change
                    const systemMessage: Message = {
                      id: `system_${Date.now()}`,
                      text: `Switched to ${
                        modelOption?.label || "Unknown model"
                      }`,
                      sender: "AI",
                      timestamp: new Date(),
                    };

                    // Add message and update model
                    setMessages((prev) => [...prev, systemMessage]);
                    setModel(newModel);
                  }
                }
              }}
              dropdownZIndex={1000}
            />
          </Box>
        </Inline>
      </Box>

      {/* Messages Container */}
      <Box
        sx={{
          flex: 1,
          padding: 3,
          overflowY: "auto",
          backgroundColor: "#F5F5F5",
          backgroundImage:
            "linear-gradient(rgba(255,255,255,0.8), rgba(255,255,255,0.8))",
        }}
        ref={messagesContainerRef}
      >
        <Stack gap="3">
          {messages.map((message) => (
            <MessageBubble key={message.id} message={message} />
          ))}
        </Stack>
      </Box>
      {/* Actions  */}
      <Inline gap="5">
        {actions.map((action) => (
          <Button
            key={action.label}
            look="tertiary"
            rounded
            onClick={() => handleSendMessage(action.message ?? action.label)}
          >
            {action.label}
          </Button>
        ))}
      </Inline>

      {/* Message Input  */}
      <Inline alignX="apart">
        <Box sx={{ pt: 5, pl: 3, pb: 2, width: "635px" }}>
          <TextInput
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            placeholder="Type a message..."
            onKeyDown={(e) => {
              // Send on Enter (without modifiers) and prevent newline insertion
              if (
                e.key === "Enter" &&
                !e.shiftKey &&
                !e.altKey &&
                !e.metaKey &&
                !e.ctrlKey
              ) {
                e.preventDefault();
                if (newMessage.trim() !== "") {
                  handleSendMessage();
                }
              }

              // Handle up arrow key to navigate message history
              if (e.key === "ArrowUp") {
                e.preventDefault();
                // Only proceed if there's history to navigate
                if (messageHistory.length > 0) {
                  // Calculate the next index (with wrapping)
                  const nextIndex = Math.min(
                    historyIndex + 1,
                    messageHistory.length - 1,
                  );
                  // Update the index and set the message text to the historical message
                  setHistoryIndex(nextIndex);
                  setNewMessage(messageHistory[nextIndex]);
                }
              }

              // Handle down arrow key to navigate back through message history
              if (e.key === "ArrowDown") {
                e.preventDefault();
                if (historyIndex > 0) {
                  // Move backward in history
                  const nextIndex = historyIndex - 1;
                  setHistoryIndex(nextIndex);
                  setNewMessage(messageHistory[nextIndex]);
                } else if (historyIndex === 0) {
                  // If we're at the beginning of history, clear the input
                  setHistoryIndex(-1);
                  setNewMessage("");
                }
              }
            }}
          />
        </Box>
        <Box sx={{ pt: 5, pb: 2, pr: 5 }}>
          <Button
            iconLeft={PaperplaneSolidIcon}
            onClick={() => handleSendMessage()}
            disabled={newMessage.trim() === ""}
            sx={{ marginLeft: 2 }}
          />
        </Box>
      </Inline>
    </Box>
  );
};

interface MessageBubbleProps {
  message: Message;
}

// Very lightweight formatter to clean agent traces and present only the final answer nicely.
// It extracts the content after 'Final Answer:' and builds paragraphs and bullet lists.
const formatAssistantContent = (raw: string) => {
  if (!raw) return raw;
  // If we have a Final Answer section, isolate it; otherwise show the raw text (unless it's a placeholder like Thinking...)
  const thinkingPlaceholders = ["Thinking...", "(No response received)"]; // leave untouched
  if (thinkingPlaceholders.includes(raw.trim())) return raw;

  let content = raw;
  const marker = "Final Answer:";
  if (raw.includes(marker)) {
    content = raw.split(marker).slice(1).join(marker).trim();
  }

  // Remove obvious agent protocol lines if any leaked into final segment
  const filteredLines = content
    .split(/\r?\n/)
    .map((l) => l.trimEnd())
    .filter((l) => !/^(Thought|Action|Action Input|Observation):/i.test(l)); // we'll handle blank separation later

  // Reconstruct with blank line separators preserved for paragraph detection
  // We'll rebuild paragraphs: split on blank lines from original content (keeping order)
  const paragraphs: string[] = [];
  let current: string[] = [];
  for (const line of filteredLines) {
    if (line.trim() === "") {
      if (current.length) {
        paragraphs.push(current.join("\n"));
        current = [];
      }
    } else {
      current.push(line);
    }
  }
  if (current.length) paragraphs.push(current.join("\n"));

  // Convert paragraphs that are pure bullet series into lists
  const elements = paragraphs.map((p, idx) => {
    const lines = p.split(/\n/);
    const bulletLines = lines.filter((l) => /^[-*]\s+/.test(l));
    if (bulletLines.length === lines.length && bulletLines.length > 0) {
      return (
        <Box as="ul" key={idx} sx={{ pl: 4, mt: 2, mb: 2 }}>
          {bulletLines.map((bl, i) => (
            <li key={i} style={{ marginBottom: "4px" }}>
              <Text as="span" sx={{ color: "brandDark.80" }}>
                {bl.replace(/^[-*]\s+/, "")}
              </Text>
            </li>
          ))}
        </Box>
      );
    }
    // Simple heading detection (lines ending with ':' and single line paragraph)
    if (lines.length === 1 && /:$/u.test(lines[0]) && lines[0].length < 80) {
      return (
        <Text key={idx} sx={{ fontWeight: 600, mt: idx === 0 ? 0 : 3, mb: 1 }}>
          {lines[0].replace(/:$/, "")}
        </Text>
      );
    }
    return (
      <Text key={idx} sx={{ mt: idx === 0 ? 0 : 2, whiteSpace: "pre-wrap" }}>
        {p}
      </Text>
    );
  });

  return elements.length ? elements : raw;
};

const MessageBubble: React.FC<MessageBubbleProps> = ({ message }) => {
  const isUserMessage = message.sender === "Human";
  const isSystemMessage = message.id.startsWith("system_");

  // For system messages (like model changes), show a centered notification
  if (isSystemMessage) {
    return (
      <Box
        data-message="true"
        sx={{
          display: "flex",
          justifyContent: "center",
          marginBottom: 1,
          marginTop: 1,
        }}
      >
        <Box
          sx={{
            backgroundColor: "brandDark.10",
            borderRadius: "16px",
            padding: "4px 12px",
            position: "relative",
            boxShadow: "0px 1px 2px rgba(0, 0, 0, 0.05)",
            display: "flex",
            alignItems: "center",
          }}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{ marginRight: "6px" }}
          >
            <path d="M22 12h-4l-3 9L9 3l-3 9H2" />
          </svg>
          <Text size="xs" sx={{ color: "brandDark.70", fontStyle: "italic" }}>
            {message.text}
          </Text>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      data-message="true"
      sx={{
        display: "flex",
        justifyContent: isUserMessage ? "flex-end" : "flex-start",
        marginBottom: 1,
      }}
    >
      {!isUserMessage && (
        <Box
          sx={{
            height: "33px",
            alignItems: "center",
            display: "flex",
            mr: 5,
            mt: 2,
            p: 3,
            color: "primary.80",
            borderRadius: "18px",
            backgroundColor: "primary.10",
          }}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="lucide lucide-bot-icon lucide-bot"
          >
            <path d="M12 8V4H8" />
            <rect width="16" height="12" x="4" y="8" rx="2" />
            <path d="M2 14h2" />
            <path d="M20 14h2" />
            <path d="M15 13v2" />
            <path d="M9 13v2" />
          </svg>
        </Box>
      )}
      <Box
        sx={{
          maxWidth: "70%",
          backgroundColor: isUserMessage ? "primary.80" : "white",
          borderRadius: "8px",
          padding: "8px 12px",
          position: "relative",
          boxShadow: "0px 1px 2px rgba(0, 0, 0, 0.1)",

          "&::after": {
            position: "absolute",
            width: "0",
            height: "0",
            top: "10px",
            border: "8px solid transparent",
            ...(isUserMessage
              ? { right: "-8px", borderLeftColor: "primary.80" }
              : { left: "-8px", borderRightColor: "white" }),
          },
        }}
      >
        {isUserMessage ? (
          <Text sx={{ color: "white" }}>{message.text}</Text>
        ) : (
          <Box sx={{ color: "brandDark.80" }}>
            {formatAssistantContent(message.text)}
          </Box>
        )}
        <Text
          size="xs"
          sx={{
            textAlign: "right",
            marginTop: 1,
            display: "block",
            color: isUserMessage ? "white" : "brandDark.80",
          }}
        >
          {formatTime(message.timestamp)}
        </Text>
      </Box>
      {isUserMessage && (
        <Box
          sx={{
            height: "33px",
            alignItems: "center",
            display: "flex",
            ml: 5,
            mt: 2,
            p: 3,
            color: "white",
            borderRadius: "18px",
            backgroundColor: "primary.80",
          }}
        >
          <UserOutlineIcon />
        </Box>
      )}
    </Box>
  );
};

export default ChatComponent;
